<template>
  <div class="card">
    <div class="card-body">
      <div v-if="recordings && recordings.length > 0">
        <!--Filter button section-->
        <div class="filter-buttons mb-3 mt-3">
          <button 
            class="button" 
            v-for="filter in filters" 
            :key="filter.name"
            @click="toggleFilter(filter)"
          >
            <div class="checkbox" :class="{ checked: filter.checked }"></div>
            <div v-if="filter.type === 'icon'">
              <span v-if="filter.icon==='inbound-call'">  <i class="fas fa-phone-alt icon-class icons"></i> </span>
              <span v-else><i class="fas fa-phone icon-class icons"></i></span>
            </div>
            <div v-else-if="filter.type === 'color'" class="rectangle" :style="{ backgroundColor: filter.color }"></div>
            <span>{{ filter.name }}</span>
          </button>
        </div>
        <!--Table layout section-->
        <table class="table">
        <thead>
          <tr>
            <th class="icon-column "></th>
            <th class="date-column center">Date</th>
            <th class="phone-column center">Caller</th>
            <th class="phone-column center">Callee</th>
            <th class="transcript-column center">Transcript</th>
            <th v-if="tokenCan('admin')" class="audio-column center">Audio</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="record in filteredCalls" :key="record.id">
            <td :class="record.type + ' icon-column'">
              <div v-if="record.direction === 'inbound'">
                <i class="fas fa-phone-alt icon-style icons"></i>
              </div>
              <div v-else-if="record.direction === 'outbound'">
                   <i class="fas fa-phone icon-style icons"></i>
              </div>
            </td>
            <td class="date-column center">{{ record.callDate }}</td>
            <td class="phone-column center">{{ record.callerNumber | formatPhone}}</td>
            <td class="phone-column center">{{ record.calleeNumber | formatPhone}}</td>
            <td class="transcription transcript-column">
              <div @click="toggleExpand(record.id)">{{ expandedTranscriptions.includes(record.id) ? record.transcript : record.transcript.slice(0, 60) + '...' }}</div>
             </td>
             <td v-if="tokenCan('admin')" class="audio-column">
                <button 
                  class="btn btn-sm" 
                  @click="openModal(record.recordingUrl, record.recordingId)"
                >
                  <i class="far fa-play-circle icons" ></i>
                </button>
             </td>
          </tr>
        </tbody>
      </table>
      </div>
      <div v-else class="alert alert-info mb-0">No recordings have been added yet.</div>
      <AudioModal
          v-if="isModalOpen"
          :audioUrl="selectedAudioUrl"
          :audioId="selectedAudioId"
          @close="closeModal"
        />
    </div>
  </div>
</template>

<script >
import 'floating-vue/dist/style.css';
import AudioModal from './modals/AudioModal.vue';
import permissionsMixin from '/src/mixins/permissions';
import { ApiClient } from '../../api/ApiClient';    

export default {
  mixins: [permissionsMixin],
    props: {
    leadlikeId: {
      type: [Number, String],
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    AudioModal,
  },
  data() {
    return {
      recordings: [],
      loading: false,
      filters: [
        { type: 'icon', name: 'Inbound', icon: 'inbound-call', checked: true },
        { type: 'icon', name: 'Outbound', icon: 'outbound-call', checked: true },
        { type: 'color', name: 'Customers', color: '#12E3FF', checked: true },
        { type: 'color', name: 'Carrier', color: '#6A76DF', checked: true },
      ],
      checkedFilters: ['Inbound', 'Outbound', 'Customers', 'Carrier'],
      isModalOpen: false,
      selectedAudioUrl: null,
      selectedAudioId: null,
      expandedTranscriptions: [],
    };
  },

  computed: {
    filteredCalls() {
      if (this.checkedFilters.length === 0) return [];

      const inboundChecked = this.checkedFilters.includes('Inbound');
      const outboundChecked = this.checkedFilters.includes('Outbound');
      const customersChecked = this.checkedFilters.includes('Customers');
      const carrierChecked = this.checkedFilters.includes('Carrier');

      // If ALL filters are selected → show everything
      if (inboundChecked && outboundChecked && customersChecked && carrierChecked) {
        return this.recordings;
      }

      return this.recordings.filter(record => {
        // direction condition
        const matchDirection =
          (inboundChecked && record.direction === 'inbound') ||
          (outboundChecked && record.direction === 'outbound') ||
          (!inboundChecked && !outboundChecked); // if no direction filters checked → ignore direction

        // type condition
        const matchType =
          (customersChecked && record.type === 'customer') ||
          (carrierChecked && record.type === 'carrier') ||
          (!customersChecked && !carrierChecked); // if no type filters checked → ignore type

        return matchDirection && matchType;
      });
    }
  },
  methods: {
    toggleFilter(filter) {
      filter.checked = !filter.checked;
      this.checkedFilters = this.filters.filter(f => f.checked).map(f => f.name);
    },
    toggleExpand(recordId) {
      if (this.expandedTranscriptions.includes(recordId)) {
        this.expandedTranscriptions = this.expandedTranscriptions.filter(id => id !== recordId);
      } else {
        this.expandedTranscriptions.push(recordId);
      }
    },
    async fetch() {
      this.loading = true;
      try {
        const response = await ApiClient.leads.recordings.get({ leadId: this.leadlikeId, params: { page: 1, perPage: 200 } });
        this.recordings = response.data.data;
      } catch (error) {
        console.error('Error fetching recordings:', error);
      }
      this.loading = false;
    },    
    async changePage() {
      await this.fetch();
      this.$nextTick(() => {
        this.$refs.recordings.scrollIntoView({ block: 'start', behavior: 'smooth' });
      });
    },
    openModal(audioUrl, audioId) {
      this.selectedAudioUrl = audioUrl;
      this.selectedAudioId = audioId;
      this.isModalOpen = true;
    },
    closeModal() {
      this.isModalOpen = false;
      this.selectedAudioUrl = null;
      this.selectedAudioId = null;
    },
  },

  watch: {
    leadlikeId() {
      this.fetch();
    },
  },

  created() {
    this.fetch();
  },
};
</script>
<style scoped>
/* CSS for table */
.table {
  table-layout: auto;
  width: 100%;
}

.icon-column {
  width: 30px;
  min-width: 30px;
  max-width: 30px;
  text-align: center;
}

.date-column {
  width: 10%;
  white-space: normal;
  word-wrap: break-word;
}

.phone-column {
  width: auto;
  min-width: fit-content;
  white-space: nowrap;
}
.audio-column {
  width: 60px;
  min-width: 60px;
  max-width: 60px;
  text-align: center;
}
.transcript-column {
  width: auto;
  min-width: 160px;
}
.transcription{
  white-space: pre-line; 
}
.center{
  text-align: center;
}

/** Buttons CSS **/
.filter-buttons {
  display: flex;
  flex-direction: row; 
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
  color: #4E5155;
}
.filter-buttons button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border: 1px solid #181C210F;
  border-radius: 100px;
  background-color: #F9F9F9;
  color: #333;
  cursor: pointer;
  transition: all 0.2s ease;
}

/** CSS for ikons **/
.checkbox {
  width: 19px;
  height: 19px;
  background-color: #fff; 
  border: 2px solid #4E5155; 
  border-radius: 2px;
  position: relative;
  box-sizing: border-box;
}

.checkbox.checked {
  background-color: #4E5155; 
  border-color: #4E5155;
}

.checkbox.checked::after {
  content: "";
  position: absolute;
  left: 4px;
  top: 0px;
  width: 6px;
  height: 12px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.rectangle { 
  width: 6px; 
  height: 17px;
 }
.customer {
  border-left-width: 3px;
  border-left-style: solid;
  border-left-color: #12E3FF !important ;
}
.carrier {
  border-left-width: 3px;
  border-left-style: solid;
  border-left-color: #6A76DF !important;
}
.icons{
   color:#A3A4A6;
}
</style>
